import { Dispatch, FC, SetStateAction, useContext } from "react";
import { Box, makeStyles, Tooltip, useMediaQuery } from "@material-ui/core";
import { includes } from "lodash";

import { Button } from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { AppContext } from "contexts";
import { goToListingDealPage } from "lib/helpers";
import { LIVE_OFFER_STATES } from "services/offers/constants";
import theme, { Theme } from "theme";

const useStyles = makeStyles<Theme, { isAcceptingFinalOffers: boolean }>(
  () => ({
    container: {
      display: "flex",
      flexDirection: "column",
      gap: `${theme.spacing(3)}px`,
      width: "100%",
    },
  }),
);

interface ListingButtonsProps {
  listingId: number;
  handleOpenEditBuyNowPriceModal: () => void;
  setIsConfirmBuyModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsEditMinimumPriceModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsLogInRequiredModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsMakeOfferModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsSetBuyNowPriceModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsVerificationModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsAAIVerificationModalOpen: Dispatch<SetStateAction<boolean>>;
}

const ListingButtons: FC<ListingButtonsProps> = ({
  listingId,
  handleOpenEditBuyNowPriceModal,
  setIsConfirmBuyModalOpen,
  setIsEditMinimumPriceModalOpen,
  setIsLogInRequiredModalOpen,
  setIsMakeOfferModalOpen,
  setIsSetBuyNowPriceModalOpen,
  setIsVerificationModalOpen,
  setIsAAIVerificationModalOpen,
}) => {
  const { listing, handleRefreshListing } = useAssetDetailData(listingId);

  const {
    accept_final_offers,
    buy_now_price,
    deal,
    is_owner,
    offers,
    state,
  } = listing;
  const classes = useStyles({
    isAcceptingFinalOffers: state === "accepting_final_offers",
  });

  const isMdUp = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const { user } = useContext(AppContext);
  const hasBuyListingsPermission =
    user && includes(user.permissionList, "buy_listings");

  const isListingActive = state === "open";
  const isAcceptingFinalOffers = state === "accepting_final_offers";

  const hasDeal = !!deal;
  const mayViewDealDetails = is_owner || deal?.buyer_id === user?.userId;

  const userHasActiveOffer = offers.filter(
    offer =>
      offer.buyer_id === user?.userId &&
      LIVE_OFFER_STATES.includes(offer.state),
  )[0];

  const canPlaceFinalOffers =
    user &&
    ((user.isAAI && !is_owner) ||
      offers.some(offer => offer.buyer_id === user.userId));

  return (
    <Box className={classes.container}>
      {is_owner && !buy_now_price && isListingActive && (
        <Button
          id="btn-set-list-price"
          variant="contained"
          customColor="green"
          size="large"
          fullWidth
          onClick={() => {
            handleRefreshListing();
            setIsSetBuyNowPriceModalOpen(true);
          }}
        >
          Set List Price
        </Button>
      )}
      {is_owner && buy_now_price && isListingActive && (
        <Button
          id="btn-edit-list-price"
          variant="contained"
          customColor="green"
          size="large"
          fullWidth
          onClick={() => handleOpenEditBuyNowPriceModal()}
        >
          Edit List Price
        </Button>
      )}
      {is_owner && !(listing.kind === "direct_listing") && isListingActive && (
        <Button
          customColor="green"
          variant="contained"
          size="large"
          fullWidth
          onClick={() => setIsEditMinimumPriceModalOpen(true)}
        >
          Edit Minimum Bid
        </Button>
      )}
      {!is_owner && isListingActive && (
        <Tooltip
          title={
            !buy_now_price
              ? "This listing doesn't have a list price."
              : !hasBuyListingsPermission
              ? "Please contact us to verify your account."
              : ""
          }
        >
          <span>
            <Button
              id="buy-now"
              variant="contained"
              customColor="green"
              size="large"
              fullWidth
              onClick={() =>
                !user
                  ? setIsLogInRequiredModalOpen(true)
                  : !hasBuyListingsPermission
                  ? setIsVerificationModalOpen(true)
                  : setIsConfirmBuyModalOpen(true)
              }
              disabled={user && hasBuyListingsPermission && !buy_now_price}
            >
              {accept_final_offers ? "Offer at List" : "Buy Now"}
            </Button>
          </span>
        </Tooltip>
      )}
      {hasDeal && mayViewDealDetails && (
        <Box my={2}>
          <Button
            customColor="green"
            variant="contained"
            size="large"
            fullWidth
            onClick={() => goToListingDealPage(deal.id)}
          >
            {isMdUp ? "See Deal Details" : "Deal Details"}
          </Button>
        </Box>
      )}
      {hasDeal && !mayViewDealDetails && (
        <Tooltip title="This asset is not available">
          <Box my={2}>
            <Button
              disabled
              customColor="green"
              variant="contained"
              size="large"
              fullWidth
            >
              Unavailable
            </Button>
          </Box>
        </Tooltip>
      )}
      {!is_owner && isListingActive && (
        <Tooltip
          title={
            !!userHasActiveOffer && isListingActive
              ? "You can't have more than one active offer."
              : ""
          }
        >
          <span>
            <Button
              customColor="green"
              variant="contained"
              size="large"
              fullWidth
              onClick={
                hasBuyListingsPermission
                  ? () => setIsMakeOfferModalOpen(true)
                  : user
                  ? () => setIsVerificationModalOpen(true)
                  : () => setIsLogInRequiredModalOpen(true)
              }
              id="make-offer"
              disabled={!!userHasActiveOffer && isListingActive}
            >
              Make Offer
            </Button>
          </span>
        </Tooltip>
      )}
      {!is_owner && isAcceptingFinalOffers && (
        <Button
          customColor="green"
          variant="contained"
          size="large"
          fullWidth
          onClick={() =>
            !user
              ? setIsLogInRequiredModalOpen(true)
              : canPlaceFinalOffers
              ? setIsMakeOfferModalOpen(true)
              : setIsAAIVerificationModalOpen(true)
          }
          id="make-offer"
        >
          Bid Now
        </Button>
      )}
    </Box>
  );
};

export default ListingButtons;
